import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  buildStateFromStructure,
  emptyForumState,
  grantToLocal,
  mapChat,
  mapForum,
  mapMessage,
  mapReply,
  mapSection,
  mapTopic,
} from '../../services/communityApiMappers';
import {
  createCommunityChannelFull,
  createCommunityReply,
  createCommunitySection,
  createCommunityTopic,
  deleteCommunityChannel,
  deleteCommunityMessage,
  deleteCommunityReply,
  deleteCommunitySection,
  deleteCommunityTopic,
  fetchCommunityChannelGrants,
  fetchCommunityMessages,
  fetchCommunityReplies,
  fetchCommunityStructure,
  fetchCommunityTopics,
  grantCommunityChannelAccess,
  reorderCommunityChannels,
  reorderCommunitySections,
  revokeCommunityChannelAccess,
  updateCommunityChannel,
  updateCommunityMessage,
  updateCommunityReply,
  updateCommunitySection,
  updateCommunityTopic,
  createCommunityMessage,
} from '../../services/communityService';
import {
  getActiveForums,
  getActiveHeaders,
  getChatMessages,
  getPinnedTopics as selectPinnedTopics,
  getPopularTopics as selectPopularTopics,
  getRecentSummaries as selectRecentSummaries,
  getRepliesForTopic,
  getTopicsForSection,
  getVisibleChatsForHeader,
  getVisibleForumsForHeader,
  type ChatInput,
  type ForumInput,
  type HeaderInput,
  type TopicInput,
} from './communityForumStore';
import type {
  AuthorProfile,
  ChannelAccessGrant,
  ChannelKind,
  CommunityChat,
  CommunityForum,
  CommunityForumState,
  CommunitySidebarHeader,
  CommunityTopic,
  TopicReply,
  TopicStatus,
  TopicSummary,
} from './types';

type ForumContextValue = {
  state: CommunityForumState;
  activeHeaders: CommunitySidebarHeader[];
  activeForums: CommunityForum[];
  isAdmin: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
  loadForumTopics: (forumId: string) => Promise<void>;
  loadTopicReplies: (topicId: string) => Promise<void>;
  loadChatMessages: (chatId: string, since?: string) => Promise<void>;
  addHeader: (input: HeaderInput) => Promise<void>;
  editHeader: (id: string, input: Partial<HeaderInput>) => Promise<void>;
  removeHeader: (id: string) => Promise<void>;
  moveHeaders: (orderedIds: string[]) => Promise<void>;
  addForum: (input: ForumInput) => Promise<void>;
  editForum: (id: string, input: Partial<ForumInput>) => Promise<void>;
  removeForum: (id: string) => Promise<void>;
  moveForums: (orderedIds: string[]) => Promise<void>;
  addChat: (input: ChatInput) => Promise<void>;
  editChat: (id: string, input: Partial<ChatInput>) => Promise<void>;
  removeChat: (id: string) => Promise<void>;
  moveChats: (orderedIds: string[]) => Promise<void>;
  addChatMessage: (chatId: string, body: string, author: AuthorProfile, authorKey?: string | null) => Promise<void>;
  editChatMessage: (messageId: string, body: string) => Promise<void>;
  removeChatMessage: (messageId: string) => Promise<void>;
  addTopic: (input: TopicInput) => Promise<CommunityTopic | null>;
  addReply: (topicId: string, body: string, author: TopicReply['author'], authorKey?: string | null) => Promise<void>;
  editReply: (replyId: string, body: string) => Promise<void>;
  setTopicStatus: (topicId: string, status: TopicStatus) => Promise<void>;
  addSummary: (topicId: string, summary: Omit<TopicSummary, 'updatedAt'>) => Promise<void>;
  removeTopic: (topicId: string) => Promise<void>;
  removeReply: (replyId: string) => Promise<void>;
  getForumsByHeader: (headerId: string) => CommunityForum[];
  getChatsByHeader: (headerId: string) => CommunityChat[];
  getForumTopics: (forumId: string) => CommunityTopic[];
  getTopicReplies: (topicId: string) => TopicReply[];
  getMessages: (chatId: string) => ReturnType<typeof getChatMessages>;
  getChannelAccessGrants: (kind: ChannelKind, channelId: string) => ChannelAccessGrant[];
  grantChannelAccessToUser: (
    kind: ChannelKind,
    channelId: string,
    userEmail: string,
    userLabel: string,
    userId?: string,
  ) => Promise<void>;
  revokeChannelAccessForUser: (kind: ChannelKind, channelId: string, userEmail: string) => Promise<void>;
  revokeChannelAccessGrant: (grantId: string) => Promise<void>;
  getPinnedTopics: () => CommunityTopic[];
  getPopularTopics: () => CommunityTopic[];
  getRecentSummaries: () => CommunityTopic[];
  activeSections: CommunityForum[];
  addSection: (input: ForumInput) => Promise<void>;
  editSection: (id: string, input: Partial<ForumInput>) => Promise<void>;
  removeSection: (id: string) => Promise<void>;
  moveSection: (orderedIds: string[]) => Promise<void>;
  getSectionTopics: (forumId: string) => CommunityTopic[];
};

const ForumContext = createContext<ForumContextValue | null>(null);

export function CommunityForumProvider({
  children,
  isAdmin,
  viewerEmail,
}: {
  children: React.ReactNode;
  isAdmin: boolean;
  viewerEmail?: string | null;
}) {
  const [state, setState] = useState<CommunityForumState>(() => emptyForumState());
  const [loading, setLoading] = useState(true);

  const reloadStructure = useCallback(async () => {
    setLoading(true);
    const res = await fetchCommunityStructure();
    if (!res.ok) {
      setLoading(false);
      return;
    }
    const base = buildStateFromStructure(res.sections, res.channels);
    setState((prev) => ({
      ...prev,
      headers: base.headers,
      forums: base.forums,
      chats: base.chats,
    }));

    const forumIds = base.forums.map((f) => f.id);
    const topicBatches = await Promise.all(forumIds.map((id) => fetchCommunityTopics(id)));
    const topics = topicBatches.flatMap((batch) =>
      batch.ok ? batch.topics.map((t) => mapTopic(t)) : [],
    );
    setState((prev) => ({ ...prev, topics }));
    setLoading(false);
  }, []);

  useEffect(() => {
    void reloadStructure();
  }, [reloadStructure]);

  const loadForumTopics = useCallback(async (forumId: string) => {
    const res = await fetchCommunityTopics(forumId);
    if (!res.ok) return;
    const mapped = res.topics.map((t) => mapTopic(t));
    setState((prev) => ({
      ...prev,
      topics: [...prev.topics.filter((t) => t.sectionId !== forumId), ...mapped],
    }));
  }, []);

  const loadTopicReplies = useCallback(async (topicId: string) => {
    const res = await fetchCommunityReplies(topicId);
    if (!res.ok) return;
    const mapped = res.replies.map((r) => mapReply(r));
    setState((prev) => ({
      ...prev,
      replies: [...prev.replies.filter((r) => r.topicId !== topicId), ...mapped],
    }));
  }, []);

  const loadChatMessages = useCallback(async (chatId: string, since?: string) => {
    const res = await fetchCommunityMessages(chatId, since);
    if (!res.ok) return;
    const mapped = res.messages.map((m) => mapMessage(m, viewerEmail || undefined));
    setState((prev) => {
      if (since) {
        const existing = prev.chatMessages.filter((m) => m.chatId === chatId);
        const merged = [...existing];
        mapped.forEach((msg) => {
          if (!merged.some((m) => m.id === msg.id)) merged.push(msg);
        });
        return {
          ...prev,
          chatMessages: [...prev.chatMessages.filter((m) => m.chatId !== chatId), ...merged],
        };
      }
      return {
        ...prev,
        chatMessages: [...prev.chatMessages.filter((m) => m.chatId !== chatId), ...mapped],
      };
    });
  }, [viewerEmail]);

  const syncGrantsForChannel = useCallback(async (kind: ChannelKind, channelId: string) => {
    const res = await fetchCommunityChannelGrants(channelId);
    if (!res.ok) return;
    const grants = res.data.map((g) => grantToLocal(kind, channelId, g));
    setState((prev) => ({
      ...prev,
      accessGrants: [
        ...prev.accessGrants.filter((g) => !(g.channelKind === kind && g.channelId === channelId)),
        ...grants,
      ],
    }));
  }, []);

  const value = useMemo((): ForumContextValue => ({
    state,
    activeHeaders: getActiveHeaders(state),
    activeForums: getActiveForums(state).filter((f) =>
      getVisibleForumsForHeader(state, f.headerId, viewerEmail, isAdmin).some((vf) => vf.id === f.id),
    ),
    isAdmin,
    loading,
    refresh: reloadStructure,
    loadForumTopics,
    loadTopicReplies,
    loadChatMessages,
    addHeader: async (input) => {
      const res = await createCommunitySection({
        title: input.title,
        sort_order: input.sortOrder,
        status: input.status,
      });
      if (res.ok) await reloadStructure();
    },
    editHeader: async (id, input) => {
      const res = await updateCommunitySection(id, {
        title: input.title,
        sort_order: input.sortOrder,
        status: input.status,
      });
      if (res.ok) {
        setState((prev) => ({
          ...prev,
          headers: prev.headers.map((h) =>
            h.id === id
              ? mapSection(res.section)
              : h,
          ),
        }));
      }
    },
    removeHeader: async (id) => {
      const res = await deleteCommunitySection(id);
      if (res.ok) await reloadStructure();
      else window.alert('Не удалось удалить заголовок');
    },
    moveHeaders: async (orderedIds) => {
      const res = await reorderCommunitySections(orderedIds);
      if (res.ok) await reloadStructure();
    },
    addForum: async (input) => {
      const res = await createCommunityChannelFull({
        name: input.name,
        section_id: input.headerId,
        channel_type: 'forum',
        description: input.description,
        icon: input.icon,
        forum_type: input.type,
        access_mode: input.viewAccess,
        create_topics_access: input.createTopicsAccess,
        reply_access: input.replyAccess,
      });
      if (res.ok) await reloadStructure();
    },
    editForum: async (id, input) => {
      const res = await updateCommunityChannel(id, {
        section_id: input.headerId,
        name: input.name,
        description: input.description,
        icon: input.icon,
        forum_type: input.type,
        status: input.status,
        sort_order: input.sortOrder,
        access_mode: input.viewAccess,
        create_topics_access: input.createTopicsAccess,
        reply_access: input.replyAccess,
      });
      if (res.ok) {
        setState((prev) => ({
          ...prev,
          forums: prev.forums.map((f) => (f.id === id ? mapForum(res.channel) : f)),
        }));
      }
    },
    removeForum: async (id) => {
      await deleteCommunityChannel(id);
      await reloadStructure();
    },
    moveForums: async (orderedIds) => {
      await reorderCommunityChannels(orderedIds);
      await reloadStructure();
    },
    addChat: async (input) => {
      const res = await createCommunityChannelFull({
        name: input.name,
        section_id: input.headerId,
        channel_type: 'chat',
        description: input.description,
        icon: input.icon,
        access_mode: input.viewAccess,
        post_access: input.postAccess,
      });
      if (res.ok) await reloadStructure();
    },
    editChat: async (id, input) => {
      const res = await updateCommunityChannel(id, {
        section_id: input.headerId,
        name: input.name,
        description: input.description,
        icon: input.icon,
        status: input.status,
        sort_order: input.sortOrder,
        access_mode: input.viewAccess,
        post_access: input.postAccess,
      });
      if (res.ok) {
        setState((prev) => ({
          ...prev,
          chats: prev.chats.map((c) => (c.id === id ? mapChat(res.channel) : c)),
        }));
      }
    },
    removeChat: async (id) => {
      await deleteCommunityChannel(id);
      await reloadStructure();
    },
    moveChats: async (orderedIds) => {
      await reorderCommunityChannels(orderedIds);
      await reloadStructure();
    },
    addChatMessage: async (chatId, body, _author, authorKey) => {
      const res = await createCommunityMessage(chatId, body, false);
      if (!res.ok) return;
      const msg = mapMessage(res.message, authorKey || viewerEmail || undefined);
      setState((prev) => ({ ...prev, chatMessages: [...prev.chatMessages, msg] }));
    },
    editChatMessage: async (messageId, body) => {
      const msg = state.chatMessages.find((m) => m.id === messageId);
      if (!msg) return;
      const res = await updateCommunityMessage(msg.chatId, messageId, body);
      if (!res.ok) return;
      const mapped = mapMessage(res.message, msg.authorKey);
      setState((prev) => ({
        ...prev,
        chatMessages: prev.chatMessages.map((m) => (m.id === messageId ? mapped : m)),
      }));
    },
    removeChatMessage: async (messageId) => {
      const msg = state.chatMessages.find((m) => m.id === messageId);
      if (!msg) return;
      const res = await deleteCommunityMessage(msg.chatId, messageId);
      if (!res.ok) return;
      setState((prev) => ({
        ...prev,
        chatMessages: prev.chatMessages.filter((m) => m.id !== messageId),
      }));
    },
    addTopic: async (input) => {
      const res = await createCommunityTopic(input.sectionId, {
        title: input.title,
        description: input.description,
        tags: input.tags,
        anonymous: input.author?.isAnonymous,
      });
      if (!res.ok) return null;
      const topic = mapTopic(res.topic);
      setState((prev) => ({ ...prev, topics: [topic, ...prev.topics] }));
      return topic;
    },
    addReply: async (topicId, body, _author, authorKey) => {
      const res = await createCommunityReply(topicId, {
        body,
        anonymous: _author?.isAnonymous,
      });
      if (!res.ok) return;
      const reply = mapReply(res.reply, authorKey || viewerEmail || undefined);
      setState((prev) => ({
        ...prev,
        replies: [...prev.replies, reply],
        topics: prev.topics.map((t) =>
          t.id === topicId
            ? { ...t, replyCount: t.replyCount + 1, lastActivityAt: reply.createdAt }
            : t,
        ),
      }));
    },
    editReply: async (replyId, body) => {
      const res = await updateCommunityReply(replyId, body);
      if (!res.ok) return;
      const mapped = mapReply(res.reply);
      setState((prev) => ({
        ...prev,
        replies: prev.replies.map((r) => (r.id === replyId ? mapped : r)),
      }));
    },
    setTopicStatus: async (topicId, status) => {
      const res = await updateCommunityTopic(topicId, { status });
      if (!res.ok) return;
      const mapped = mapTopic(res.topic);
      setState((prev) => ({
        ...prev,
        topics: prev.topics.map((t) => (t.id === topicId ? mapped : t)),
      }));
    },
    addSummary: async (topicId, summary) => {
      const res = await updateCommunityTopic(topicId, {
        summary: { body: summary.body, prepared_by: summary.preparedBy },
      });
      if (!res.ok) return;
      const mapped = mapTopic(res.topic);
      setState((prev) => ({
        ...prev,
        topics: prev.topics.map((t) => (t.id === topicId ? mapped : t)),
      }));
    },
    removeTopic: async (topicId) => {
      const res = await deleteCommunityTopic(topicId);
      if (!res.ok) return;
      setState((prev) => ({
        ...prev,
        topics: prev.topics.filter((t) => t.id !== topicId),
        replies: prev.replies.filter((r) => r.topicId !== topicId),
      }));
    },
    removeReply: async (replyId) => {
      const reply = state.replies.find((r) => r.id === replyId);
      if (!reply) return;
      const res = await deleteCommunityReply(replyId);
      if (!res.ok) return;
      setState((prev) => ({
        ...prev,
        replies: prev.replies.filter((r) => r.id !== replyId),
        topics: prev.topics.map((t) =>
          t.id === reply.topicId ? { ...t, replyCount: Math.max(0, t.replyCount - 1) } : t,
        ),
      }));
    },
    getForumsByHeader: (headerId) => getVisibleForumsForHeader(state, headerId, viewerEmail, isAdmin),
    getChatsByHeader: (headerId) => getVisibleChatsForHeader(state, headerId, viewerEmail, isAdmin),
    getForumTopics: (forumId) => getTopicsForSection(state, forumId),
    getTopicReplies: (topicId) => getRepliesForTopic(state, topicId),
    getMessages: (chatId) => getChatMessages(state, chatId),
    getChannelAccessGrants: (kind, channelId) =>
      state.accessGrants.filter((g) => g.channelKind === kind && g.channelId === channelId),
    grantChannelAccessToUser: async (kind, channelId, _userEmail, _userLabel, userId) => {
      if (!userId) return;
      const res = await grantCommunityChannelAccess(channelId, userId);
      if (res.ok) await syncGrantsForChannel(kind, channelId);
    },
    revokeChannelAccessForUser: async (kind, channelId, _userEmail) => {
      const grant = state.accessGrants.find(
        (g) => g.channelKind === kind && g.channelId === channelId && g.userEmail === _userEmail,
      );
      if (!grant?.userId) return;
      const res = await revokeCommunityChannelAccess(channelId, grant.userId);
      if (res.ok) await syncGrantsForChannel(kind, channelId);
    },
    revokeChannelAccessGrant: async (grantId) => {
      const grant = state.accessGrants.find((g) => g.id === grantId);
      if (!grant?.userId) return;
      const res = await revokeCommunityChannelAccess(grant.channelId, grant.userId);
      if (res.ok) await syncGrantsForChannel(grant.channelKind, grant.channelId);
    },
    getPinnedTopics: () => selectPinnedTopics(state),
    getPopularTopics: () => selectPopularTopics(state),
    getRecentSummaries: () => selectRecentSummaries(state),
    activeSections: getActiveForums(state),
    addSection: async (input) => {
      await createCommunityChannelFull({
        name: input.name,
        section_id: input.headerId,
        channel_type: 'forum',
        description: input.description,
        icon: input.icon,
        forum_type: input.type,
        access_mode: input.viewAccess,
        create_topics_access: input.createTopicsAccess,
        reply_access: input.replyAccess,
      });
      await reloadStructure();
    },
    editSection: async (id, input) => {
      await updateCommunityChannel(id, {
        section_id: input.headerId,
        name: input.name,
        description: input.description,
        icon: input.icon,
        forum_type: input.type,
        status: input.status,
        sort_order: input.sortOrder,
        access_mode: input.viewAccess,
        create_topics_access: input.createTopicsAccess,
        reply_access: input.replyAccess,
      });
      await reloadStructure();
    },
    removeSection: async (id) => {
      await deleteCommunityChannel(id);
      await reloadStructure();
    },
    moveSection: async (orderedIds) => {
      await reorderCommunityChannels(orderedIds);
      await reloadStructure();
    },
    getSectionTopics: (forumId) => getTopicsForSection(state, forumId),
  }), [
    state,
    isAdmin,
    viewerEmail,
    loading,
    reloadStructure,
    loadForumTopics,
    loadTopicReplies,
    loadChatMessages,
    syncGrantsForChannel,
  ]);

  return <ForumContext.Provider value={value}>{children}</ForumContext.Provider>;
}

export function useCommunityForum() {
  const ctx = useContext(ForumContext);
  if (!ctx) throw new Error('useCommunityForum must be used within CommunityForumProvider');
  return ctx;
}
